import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { FolderInput, ArrowUp, Check, ChevronsUpDown, Folder, Search } from 'lucide-react';
import FolderDropdown from "./FolderDropdown";
import Cookies from "js-cookie";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { fetchAllFolderdetails } from "../redux/actions/folders.action";
import { connect } from "react-redux";
import { ActionButton } from "./ItemDetailsModal/CommonComponents";

import { BACKEND_API_URL } from "../utils/api";
const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const MoveFolderModal = ({ folder_id, fetchAllFolderdetails }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [parentId, setParentId] = useState(null);
    const [siblings, setSiblings] = useState([]);
    const [selectedSibling, setSelectedSibling] = useState(null);
    const [siblingPopoverOpen, setSiblingPopoverOpen] = useState(false);
    const [siblingSearchQuery, setSiblingSearchQuery] = useState('');

    const handleOpen = async () => {
        setOpen(true);
        await fetchSiblings();
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedFolderId('');
        setSelectedSibling(null);
        setSiblingSearchQuery('');
    };

    const handleFolderSelect = (folderId) => {
        setSelectedFolderId(folderId);
    };

    const fetchSiblings = async () => {
        try {
            const accessToken = Cookies.get('access_token');
            const url = `${REACT_APP_BACKEND_API_URL}/api/get_sibling_folders/${folder_id}`;
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            setSiblings(response.data.siblings || []);
            setParentId(response.data.parent);
        } catch (error) {
            console.error('Error fetching siblings:', error);
            toast.error("Error loading folder information");
        }
    };

    const handleMoveFolder = async (targetFolderId = undefined, successMessage = "Folder Moved Successfully.") => {
        const destinationId = targetFolderId !== undefined ? targetFolderId : selectedFolderId;

        if (!destinationId && destinationId !== null) {
            return;
        }

        try {
            const accessToken = Cookies.get('access_token');
            let params = "";
            if (destinationId !== null && destinationId !== undefined) {
                params = `folder_id=${destinationId}`;
            }
            const url = `${REACT_APP_BACKEND_API_URL}/api/move_item/${folder_id}?${params}`;
            await axios.get(url, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            toast.success(successMessage);
            navigate(`/folder/${folder_id}`);
            fetchAllFolderdetails(folder_id);
            handleClose();
        } catch (error) {
            console.error('Error moving folder:', error);
            toast.error("Error Moving Folder");
        }
    };

    const handleMoveToParent = async () => {
        if (parentId === null) {
            return;
        }

        try {
            const accessToken = Cookies.get('access_token');
            const parentUrl = `${REACT_APP_BACKEND_API_URL}/api/folder/${parentId}/`;
            const parentResponse = await axios.get(parentUrl, {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            const grandparentId = parentResponse.data.folder.parent;
            await handleMoveFolder(grandparentId, "Folder Moved to Parent Successfully.");
        } catch (error) {
            console.error('Error fetching parent details:', error);
            toast.error("Error Moving Folder");
        }
    };

    const handleMoveToSibling = async () => {
        if (!selectedSibling) {
            return;
        }
        await handleMoveFolder(selectedSibling.id, "Folder Moved to Sibling Successfully.");
    };

    const filteredSiblings = siblings.filter(sibling =>
        sibling.name.toLowerCase().includes(siblingSearchQuery.toLowerCase())
    );

    return (
        <>
            <ActionButton tooltip="Move" onClick={handleOpen}>
                <FolderInput className="h-4 w-4" />
            </ActionButton>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>Move Item</DialogTitle>
                        <DialogDescription>
                            Choose where to move this folder
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Quick Actions Section */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-semibold">Quick Actions</h4>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="block">
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start gap-2"
                                            onClick={handleMoveToParent}
                                            disabled={parentId === null}
                                        >
                                            <ArrowUp className="h-4 w-4" />
                                            Move to Parent
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {parentId === null
                                        ? "This folder is already at the root level and cannot be moved up"
                                        : "Move this folder one level up to its parent's parent folder"}
                                </TooltipContent>
                            </Tooltip>
                        </div>

                        <Separator />

                        {/* Move to Sibling Section */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-semibold">Move to Sibling Folder</h4>
                            {siblings.length > 0 ? (
                                <div className="space-y-2">
                                    <Popover open={siblingPopoverOpen} onOpenChange={setSiblingPopoverOpen} modal={false}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                aria-expanded={siblingPopoverOpen}
                                                className="w-full justify-between"
                                            >
                                                <span className="flex items-center gap-2 truncate">
                                                    {selectedSibling ? (
                                                        <>
                                                            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                            <span className="truncate">{selectedSibling.name}</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted-foreground">Select a sibling folder...</span>
                                                    )}
                                                </span>
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-(--radix-popover-trigger-width) p-0"
                                            align="start"
                                            onWheel={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex items-center border-b px-3">
                                                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                                <Input
                                                    placeholder="Search siblings..."
                                                    value={siblingSearchQuery}
                                                    onChange={(e) => setSiblingSearchQuery(e.target.value)}
                                                    className="border-0 focus-visible:ring-0 shadow-none"
                                                />
                                            </div>
                                            <div className="max-h-[200px] overflow-y-auto overscroll-contain">
                                                {filteredSiblings.length === 0 ? (
                                                    <div className="py-6 text-center text-sm text-muted-foreground">
                                                        No siblings found.
                                                    </div>
                                                ) : (
                                                    <div className="p-1">
                                                        {filteredSiblings.map((sibling) => (
                                                            <div
                                                                key={sibling.id}
                                                                onClick={() => {
                                                                    setSelectedSibling(sibling);
                                                                    setSiblingPopoverOpen(false);
                                                                }}
                                                                className={cn(
                                                                    "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer",
                                                                    "hover:bg-accent hover:text-accent-foreground",
                                                                    selectedSibling?.id === sibling.id && "bg-accent"
                                                                )}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "h-4 w-4 shrink-0",
                                                                        selectedSibling?.id === sibling.id ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                />
                                                                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                                <span className="truncate">{sibling.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                    <Button
                                        onClick={handleMoveToSibling}
                                        disabled={!selectedSibling}
                                        className="w-full"
                                    >
                                        Move to Sibling
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground italic py-2">
                                    No sibling folders available. This folder has no other folders at the same level.
                                </p>
                            )}
                        </div>

                        <Separator />

                        {/* Move to Any Folder Section */}
                        <div className="space-y-3">
                            <h4 className="text-sm font-semibold">Move to Any Folder</h4>
                            <FolderDropdown onFolderSelect={handleFolderSelect} folder_id={folder_id} />
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleMoveFolder()} disabled={!selectedFolderId}>
                            Move to Selected Folder
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {
    fetchAllFolderdetails
};

export default connect(mapStateToProps, mapDispatchToProps)(MoveFolderModal);
